/**
 * Modules Tab Component
 *
 * Displays discovered system modules with per-module stats and completion status
 * Supports per-module completion workflow where each module is processed end-to-end
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertCircle, Layers, Database, Code, Network, CheckCircle, Clock, Pause, Sparkles } from 'lucide-react';
import { getModuleStats, triggerModuleResearch } from '../../lib/api/enterpriseSystemsApi';
import { toast } from 'sonner';

interface ModulesTabProps {
  systemId: string;
}

const ModulesTab: React.FC<ModulesTabProps> = ({ systemId }) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['module-stats', systemId],
    queryFn: () => getModuleStats(systemId),
    // Only poll if there are modules in progress
    refetchInterval: (query) => {
      const queryData = query.state.data;
      const hasInProgress = queryData?.modules?.some((m) => m.status === 'in_progress');
      return hasInProgress ? 10000 : false; // Poll every 10 seconds only when research is active
    },
  });

  // Mutation for triggering module-specific research
  const triggerResearchMutation = useMutation({
    mutationFn: (moduleCode: string) => triggerModuleResearch(systemId, [moduleCode]),
    onSuccess: (data, moduleCode) => {
      toast.success(`Research started for module ${moduleCode}! Job ID: ${data.job_id}`, {
        duration: 6000,
      });
      // Refetch module stats to update status
      refetch();
    },
    onError: (error: any, moduleCode) => {
      toast.error(`Failed to start research for ${moduleCode}: ${error.message}`);
    },
  });

  const handleTriggerModuleResearch = (moduleCode: string, moduleName: string) => {
    if (
      window.confirm(
        `Start AI research for module "${moduleCode} - ${moduleName}"? This will discover tables, columns, and APIs for this module.`
      )
    ) {
      triggerResearchMutation.mutate(moduleCode);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
        <span className="ml-3 text-neutral-600">Loading modules...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Modules</h3>
            <p className="text-sm text-red-700 mt-1">{(error as Error).message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data || data.modules.length === 0) {
    return (
      <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-12 text-center">
        <Layers className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-neutral-900 mb-2">No Modules Found</h3>
        <p className="text-neutral-600">
          Run AI research to discover system modules automatically
        </p>
      </div>
    );
  }

  const { modules, total_modules, completed_modules, overall_stats } = data;

  // Helper functions
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'pending':
        return <Pause className="w-5 h-5 text-neutral-400" />;
      default:
        return <Layers className="w-5 h-5 text-neutral-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
            <CheckCircle className="w-3.5 h-3.5" />
            Complete
          </span>
        );
      case 'in_progress':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full border border-blue-200">
            <Clock className="w-3.5 h-3.5 animate-pulse" />
            In Progress
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-neutral-50 text-neutral-600 text-xs font-medium rounded-full border border-neutral-200">
            <Pause className="w-3.5 h-3.5" />
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
      default:
        return 'bg-neutral-100 text-neutral-700 border-neutral-300';
    }
  };

  const getPriorityLabel = (priority: string) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  return (
    <div>
      {/* Header with Summary Stats */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">System Modules</h2>
            <p className="text-sm text-neutral-600 mt-1">
              Per-module completion status ({completed_modules} of {total_modules} modules complete)
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 hover:bg-neutral-50 transition-colors flex items-center gap-2"
          >
            <Loader2 className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Overall Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Database className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-900">{overall_stats.total_tables}</p>
                <p className="text-sm text-blue-700">Total Tables</p>
              </div>
            </div>
          </div>

          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-900">{overall_stats.total_columns}</p>
                <p className="text-sm text-purple-700">Total Columns</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Code className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-900">{overall_stats.total_apis}</p>
                <p className="text-sm text-green-700">Total APIs</p>
              </div>
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Network className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-900">{overall_stats.total_patterns}</p>
                <p className="text-sm text-orange-700">Patterns</p>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="bg-neutral-100 rounded-full h-3 overflow-hidden">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-500"
            style={{ width: `${(completed_modules / total_modules) * 100}%` }}
          />
        </div>
        <p className="text-xs text-neutral-600 mt-2 text-center">
          {Math.round((completed_modules / total_modules) * 100)}% Complete
        </p>
      </div>

      {/* Module Cards */}
      <div className="space-y-4">
        {modules.map((module) => (
          <div
            key={module.module_code}
            className={`bg-white border rounded-lg p-6 transition-all hover:shadow-md ${
              module.status === 'completed'
                ? 'border-green-200'
                : module.status === 'in_progress'
                ? 'border-blue-300 shadow-sm'
                : 'border-neutral-200'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className={`p-3 rounded-lg ${
                    module.status === 'completed'
                      ? 'bg-green-50'
                      : module.status === 'in_progress'
                      ? 'bg-blue-50'
                      : 'bg-neutral-50'
                  }`}
                >
                  {getStatusIcon(module.status)}
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-lg font-semibold text-neutral-900">
                      {module.module_code} - {module.module_name}
                    </h3>
                    {getStatusBadge(module.status)}
                  </div>
                  {module.category && (
                    <p className="text-sm text-neutral-600">{module.category}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span
                  className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityColor(
                    module.priority
                  )}`}
                >
                  {getPriorityLabel(module.priority)}
                </span>

                {/* Research button - only show for pending or incomplete modules */}
                {module.status !== 'completed' && (
                  <button
                    onClick={() => handleTriggerModuleResearch(module.module_code, module.module_name)}
                    disabled={triggerResearchMutation.isPending || module.status === 'in_progress'}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-secondary-600 rounded-lg hover:bg-secondary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={module.status === 'in_progress' ? 'Research in progress' : 'Start AI research for this module'}
                  >
                    {triggerResearchMutation.isPending ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Starting...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>Research</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {module.description && (
              <p className="text-sm text-neutral-600 mb-4">{module.description}</p>
            )}

            <div className="grid grid-cols-4 gap-4 pt-4 border-t border-neutral-100">
              <div className="text-center">
                <Database className="w-5 h-5 text-neutral-400 mx-auto mb-1" />
                <p className="text-xs text-neutral-500 mb-1">Tables</p>
                <p className="text-xl font-bold text-neutral-900">{module.tables_count}</p>
              </div>
              <div className="text-center">
                <Database className="w-5 h-5 text-neutral-400 mx-auto mb-1" />
                <p className="text-xs text-neutral-500 mb-1">Columns</p>
                <p className="text-xl font-bold text-neutral-900">{module.columns_count}</p>
              </div>
              <div className="text-center">
                <Code className="w-5 h-5 text-neutral-400 mx-auto mb-1" />
                <p className="text-xs text-neutral-500 mb-1">APIs</p>
                <p className="text-xl font-bold text-neutral-900">{module.apis_count}</p>
              </div>
              <div className="text-center">
                <Network className="w-5 h-5 text-neutral-400 mx-auto mb-1" />
                <p className="text-xs text-neutral-500 mb-1">Patterns</p>
                <p className="text-xl font-bold text-neutral-900">
                  {module.integration_patterns_count}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ModulesTab;
